import type { CSSProperties, ReactNode } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { cn } from '@/lib/cn';
import { monthTarget, UNSCHEDULE_TARGET, type CalendarDrag } from '@/lib/calendarDrop';

/**
 * Pointer/touch listeners of a nested drag handle must not also start the drag of the
 * surrounding bar – stop the event before dnd-kit sees it bubble up.
 */
function isolate(listeners: Record<string, (event: never) => void> | undefined) {
  if (!listeners) return {};
  return Object.fromEntries(
    Object.entries(listeners).map(([name, handler]) => [
      name,
      (event: { stopPropagation: () => void }) => {
        event.stopPropagation();
        handler(event as never);
      },
    ]),
  );
}

/**
 * One month column of the year grid, as a full-height strip behind the rows.
 * dnd-kit matches the pointer against droppable rectangles rather than the DOM, so
 * the bars painted on top never swallow a drop.
 */
export function MonthColumn({ month, isNow }: { month: string; isNow: boolean }) {
  const { setNodeRef, isOver } = useDroppable({ id: monthTarget(month) });
  return (
    <div
      ref={setNodeRef}
      data-month={month}
      className={cn(
        'border-l border-[var(--border)] transition-colors first:border-l-0',
        isNow && 'bg-[var(--surface-2)]/60',
        isOver && 'bg-[var(--accent-soft)] outline-2 -outline-offset-2 outline-[var(--accent)]',
      )}
    />
  );
}

/** Drop zone that removes the planned months of an investment. */
export function UnscheduleZone({ children, active }: { children: ReactNode; active: boolean }) {
  const { setNodeRef, isOver } = useDroppable({ id: UNSCHEDULE_TARGET });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'rounded-[var(--radius,1rem)] transition-shadow',
        active && 'ring-2 ring-dashed ring-[var(--border-strong)]',
        isOver && 'ring-[var(--accent)]',
      )}
    >
      {children}
    </div>
  );
}

interface BarProps {
  investmentId: string;
  /** The month this segment sits in – where the window lands when it is dragged. */
  month: string;
  title: string;
  color: string;
  isStart: boolean;
  isEnd: boolean;
  canEdit: boolean;
  onOpen: () => void;
}

/**
 * One month of an investment's window. Dragging a segment moves the whole window; the
 * thin handles on the first and last month change only that edge.
 */
export function PlanBar({
  investmentId,
  month,
  title,
  color,
  isStart,
  isEnd,
  canEdit,
  onOpen,
}: BarProps) {
  const move = useDraggable({
    id: `move:${investmentId}:${month}`,
    data: { kind: 'move', investmentId, grabbedMonth: month } satisfies CalendarDrag,
    disabled: !canEdit,
  });
  const start = useDraggable({
    id: `start:${investmentId}:${month}`,
    data: { kind: 'start', investmentId } satisfies CalendarDrag,
    disabled: !canEdit || !isStart,
  });
  const end = useDraggable({
    id: `end:${investmentId}:${month}`,
    data: { kind: 'end', investmentId } satisfies CalendarDrag,
    disabled: !canEdit || !isEnd,
  });

  const style: CSSProperties = {
    background: color,
    opacity: move.isDragging ? 0.4 : undefined,
  };
  const handle =
    'absolute top-0 bottom-0 z-10 w-1.5 cursor-ew-resize rounded-sm bg-[var(--ink)] opacity-0 transition-opacity group-hover:opacity-30';

  return (
    <div
      ref={move.setNodeRef}
      {...(canEdit ? move.listeners : {})}
      className={cn(
        'group relative my-1 flex h-5 touch-manipulation items-stretch',
        // Only the outer months are rounded, so adjacent segments read as one bar.
        isStart && 'ml-0.5 rounded-l-full',
        isEnd && 'mr-0.5 rounded-r-full',
        canEdit && 'cursor-grab active:cursor-grabbing',
      )}
      style={style}
    >
      {canEdit && isStart ? (
        <span
          ref={start.setNodeRef}
          {...isolate(start.listeners as never)}
          aria-hidden="true"
          data-handle="start"
          className={cn(handle, 'left-0 rounded-l-full')}
        />
      ) : null}
      <button
        type="button"
        onClick={onOpen}
        title={title}
        aria-label={title}
        className="min-w-0 flex-1 transition-opacity hover:opacity-80"
      />
      {canEdit && isEnd ? (
        <span
          ref={end.setNodeRef}
          {...isolate(end.listeners as never)}
          aria-hidden="true"
          data-handle="end"
          className={cn(handle, 'right-0 rounded-r-full')}
        />
      ) : null}
    </div>
  );
}

/** A side-list entry that can be dragged onto the year grid. */
export function DraggableEntry({
  drag,
  canEdit,
  children,
}: {
  drag: CalendarDrag & { id: string };
  canEdit: boolean;
  children: ReactNode;
}) {
  const { id, ...data } = drag;
  const { setNodeRef, listeners, isDragging } = useDraggable({ id, data, disabled: !canEdit });
  return (
    <li
      ref={setNodeRef}
      {...(canEdit ? listeners : {})}
      className={cn(canEdit && 'cursor-grab touch-manipulation', isDragging && 'opacity-40')}
    >
      {children}
    </li>
  );
}
